import React, { useState, useEffect } from "react";
import { useLanguage } from '../context/LanguageContext';
import API_BASE_URL from '../services/apiconfig';

const content = {
  en: {
    title:           "LAMPS",
    overview:        "Overview",
    coreActivities:  "Core Activities of LAMPS",
    statistics:      "Statistics",
    servicesOffered: "Description of Services",
    lampsList:       "List of LAMPS",
    sno:             "S.No",
    name:            "Name of LAMPS",
  },
  ta: {
    title:           "LAMPS",
    overview:        "கண்ணோட்டம்",
    coreActivities:  "LAMPS இன் முக்கிய செயல்பாடுகள்",
    statistics:      "புள்ளிவிவரங்கள்",
    servicesOffered: "சேவைகளின் விளக்கம்",
    lampsList:       "LAMPS பட்டியல்",
    sno:             "வ.எண்",
    name:            "LAMPS பெயர்",
  },
};

const Lamps = () => {
  const { language } = useLanguage();
  const t = content[language];

  const [loading, setLoading] = useState(true);
  const [lampsData, setLampsData] = useState(null);

  useEffect(() => {
    fetchLampsData();
  }, []);

  const fetchLampsData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/lamps/get_lamps_content`);
      const result = await response.json();
      if (result.status === 'success' && result.data) {
        setLampsData(result.data);
      }
    } catch (error) {
      console.error('Error fetching LAMPS data:', error);
    } finally {
      setLoading(false);
    }
  };

  const pickLang = (item, engKey, taKey) =>
    language === 'en' ? (item?.[engKey] || '') : (item?.[taKey] || item?.[engKey] || '');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!lampsData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">No LAMPS data available</div>
      </div>
    );
  }

  const overview = language === 'en' ? lampsData.overview_eng : (lampsData.overview_tamil || lampsData.overview_eng);
  const coreActivities = lampsData.core_activities || [];
  const statistics = lampsData.statistics || [];
  const services = lampsData.services || [];
  const lampsList = lampsData.lamps_list || [];

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Header */}
      <div className="bg-blue-50 border-b border-blue-100">
        <div className="mx-auto px-4 py-10">
          <h1 className="text-3xl font-bold text-gray-800 text-center">{t.title}</h1>
        </div>
      </div>

      <div className="mx-auto px-4 py-8">
        <section className="bg-white p-8 rounded-lg shadow-sm space-y-10">

          {/* OVERVIEW */}
          {overview && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.overview}</h3>
              <div className="bg-blue-50 p-6 rounded-lg">
                <p className="text-gray-700 leading-relaxed">{overview}</p>
              </div>
            </div>
          )}

          {/* CORE ACTIVITIES */}
          {coreActivities.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.coreActivities}</h3>
              <div className="bg-indigo-50 p-6 rounded-lg">
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {coreActivities.map((activity, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center">
                        {index + 1}
                      </span>
                      <span className="text-gray-800 font-medium text-sm">
                        {pickLang(activity, 'value_eng', 'value_tamil')}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* STATISTICS */}
          {statistics.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.statistics}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {statistics.map((stat, index) => (
                  <div
                    key={index}
                    className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-center"
                  >
                    <p className="text-2xl font-bold text-blue-700">{stat.value || '—'}</p>
                    <p className="text-gray-600 text-xs mt-1">
                      {pickLang(stat, 'label_eng', 'label_tamil')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SERVICES */}
          {services.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.servicesOffered}</h3>
              <div className="bg-purple-50 p-6 rounded-lg">
                <div className="bg-white p-5 rounded-lg">
                  <ul className="space-y-4">
                    {services.map((service, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-purple-600 mr-3 font-bold text-lg">•</span>
                        <div>
                          <span className="text-gray-800 font-semibold text-sm">
                            {pickLang(service, 'head_eng', 'head_tamil')}
                          </span>
                          <p className="text-gray-600 text-sm mt-1">
                            {pickLang(service, 'content_eng', 'content_tamil')}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* LAMPS LIST */}
          {lampsList.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.lampsList}</h3>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-blue-600 text-white">
                      <th className="px-4 py-3 text-left font-semibold w-16">{t.sno}</th>
                      <th className="px-4 py-3 text-left font-semibold">{t.name}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lampsList.map((lamps, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-2.5 text-gray-500 font-medium">{lamps.sno}</td>
                        <td className="px-4 py-2.5 text-gray-800">{lamps.name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </section>
      </div>
    </div>
  );
};

export default Lamps;
